import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getInspections, createInspection, submitInspection, reviewInspection, deleteInspection, extractErrorMessage } from '../services/api';
import { FaPlus, FaPaperPlane, FaCheck, FaTimes, FaTrash, FaEye } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import '../styles/Inspections.css';
import { useToast } from '../components/Toast';

function Inspections({ user }) {
  const toast = useToast();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);
  const [savingSubmit, setSavingSubmit] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [formErrorCreate, setFormErrorCreate] = useState(null);
  const [formErrorSubmit, setFormErrorSubmit] = useState(null);
  const [formErrorReview, setFormErrorReview] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [newInspection, setNewInspection] = useState({
    user_id: '',
    type: 'appearance',
    instructions: '',
    deadline: ''
  });
  const [submission, setSubmission] = useState({ response: '', photo_url: '' });
  const [review, setReview] = useState({ passed: true, feedback: '', score: 10 });

  useEffect(() => {
    fetchInspections();
  }, [filter]);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const data = await getInspections({ status: filter !== 'all' ? filter : undefined });
      setInspections(data.inspections || data);
    } catch (error) {
      console.error('Error fetching inspections:', error);
      toast.error(extractErrorMessage(error, 'Inspektionen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (savingCreate) return;
    setSavingCreate(true);
    setFormErrorCreate(null);
    try {
      await createInspection({
        ...newInspection,
        deadline: newInspection.deadline ? new Date(newInspection.deadline).toISOString() : null
      });
      setNewInspection({ user_id: '', type: 'appearance', instructions: '', deadline: '' });
      setShowCreateModal(false);
      toast.success('Inspektion erstellt');
      fetchInspections();
    } catch (error) {
      console.error('Error creating inspection:', error);
      setFormErrorCreate(extractErrorMessage(error, 'Fehler beim Erstellen der Inspektion'));
    } finally {
      setSavingCreate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingSubmit) return;
    setSavingSubmit(true);
    setFormErrorSubmit(null);
    try {
      await submitInspection(selectedInspection.id, submission);
      setSubmission({ response: '', photo_url: '' });
      setSelectedInspection(null);
      setShowSubmitModal(false);
      toast.success('Inspektion eingereicht');
      fetchInspections();
    } catch (error) {
      console.error('Error submitting inspection:', error);
      setFormErrorSubmit(extractErrorMessage(error, 'Fehler beim Einreichen der Inspektion'));
    } finally {
      setSavingSubmit(false);
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (savingReview) return;
    setSavingReview(true);
    setFormErrorReview(null);
    try {
      await reviewInspection(selectedInspection.id, review);
      setReview({ passed: true, feedback: '', score: 10 });
      setSelectedInspection(null);
      setShowReviewModal(false);
      toast.success('Bewertung gespeichert');
      fetchInspections();
    } catch (error) {
      console.error('Error reviewing inspection:', error);
      setFormErrorReview(extractErrorMessage(error, 'Fehler beim Bewerten der Inspektion'));
    } finally {
      setSavingReview(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Inspektion wirklich löschen?')) return;
    try {
      await deleteInspection(id);
      toast.success('Inspektion gelöscht');
      fetchInspections();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen der Inspektion'));
    }
  };

  const closeCreateModal = () => {
    if (savingCreate) return;
    setShowCreateModal(false);
    setFormErrorCreate(null);
  };

  const closeSubmitModal = () => {
    if (savingSubmit) return;
    setShowSubmitModal(false);
    setFormErrorSubmit(null);
  };

  const closeReviewModal = () => {
    if (savingReview) return;
    setShowReviewModal(false);
    setFormErrorReview(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'pending', label: 'Ausstehend' },
      submitted: { class: 'submitted', label: 'Eingereicht' },
      reviewed: { class: 'reviewed', label: 'Bewertet' },
      passed: { class: 'passed', label: 'Bestanden' },
      failed: { class: 'failed', label: 'Nicht bestanden' }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Inspektionen</h1>
          {user.isAdmin && (
            <button className="add-btn" onClick={() => setShowCreateModal(true)}>
              <FaPlus /> Neue Inspektion
            </button>
          )}
        </div>

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Ausstehend</button>
          <button className={filter === 'submitted' ? 'active' : ''} onClick={() => setFilter('submitted')}>Eingereicht</button>
          <button className={filter === 'reviewed' ? 'active' : ''} onClick={() => setFilter('reviewed')}>Bewertet</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="inspections-table-wrapper">
            {inspections.length > 0 ? (
              <table className="inspections-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Typ</th>
                    <th>Anweisungen</th>
                    <th>Frist</th>
                    <th>Bewertung</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {inspections.map(insp => {
                    const statusBadge = getStatusBadge(insp.status);
                    return (
                      <tr key={insp.id}>
                        <td><span className={`status-badge ${statusBadge.class}`}>{statusBadge.label}</span></td>
                        <td className="type-cell">{insp.type}</td>
                        <td className="instructions-cell">{insp.instructions}</td>
                        <td>{insp.deadline ? new Date(insp.deadline).toLocaleDateString() : '-'}</td>
                        <td>{insp.score != null ? `${insp.score}/10` : '-'}</td>
                        <td>
                          <div className="table-actions">
                            {insp.status === 'pending' && (
                              <button
                                className="action-btn submit"
                                onClick={() => { setSelectedInspection(insp); setShowSubmitModal(true); }}
                                title="Einreichen"
                              >
                                <FaPaperPlane />
                              </button>
                            )}
                            {insp.status === 'submitted' && user.isAdmin && (
                              <button
                                className="action-btn review"
                                onClick={() => { setSelectedInspection(insp); setShowReviewModal(true); }}
                                title="Bewerten"
                              >
                                <FaEye />
                              </button>
                            )}
                            {user.isAdmin && (
                              <button className="action-btn delete" onClick={() => handleDelete(insp.id)} title="Löschen">
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Inspektionen gefunden</div>
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Inspektion</h2>
              {formErrorCreate && <div className="shared-form-error-banner">{formErrorCreate}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newInspection.user_id}
                    onChange={id => setNewInspection({...newInspection, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newInspection.type}
                    onChange={e => setNewInspection({...newInspection, type: e.target.value})}
                  >
                    <option value="appearance">Erscheinungsbild</option>
                    <option value="room">Raum</option>
                    <option value="behavior">Verhalten</option>
                    <option value="task_completion">Aufgabenerfüllung</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Anweisungen *</label>
                  <textarea
                    value={newInspection.instructions}
                    onChange={e => setNewInspection({...newInspection, instructions: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Frist</label>
                  <input
                    type="datetime-local"
                    value={newInspection.deadline}
                    onChange={e => setNewInspection({...newInspection, deadline: e.target.value})}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCreateModal} disabled={savingCreate}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingCreate}>
                    {savingCreate ? 'Speichert\u2026' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Submit Modal */}
        {showSubmitModal && selectedInspection && (
          <div className="modal-overlay" onClick={closeSubmitModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Inspektion einreichen</h2>
              <div className="inspection-info">
                <p><strong>Typ:</strong> {selectedInspection.type}</p>
                <p><strong>Anweisungen:</strong> {selectedInspection.instructions}</p>
              </div>
              {formErrorSubmit && <div className="shared-form-error-banner">{formErrorSubmit}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Antwort *</label>
                  <textarea
                    value={submission.response}
                    onChange={e => setSubmission({...submission, response: e.target.value})}
                    required
                    placeholder="Beschreibe deine Einreichung..."
                  />
                </div>
                <div className="form-group">
                  <label>Foto-URL (optional)</label>
                  <input
                    type="text"
                    value={submission.photo_url}
                    onChange={e => setSubmission({...submission, photo_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeSubmitModal} disabled={savingSubmit}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingSubmit}>
                    {savingSubmit ? 'Speichert\u2026' : 'Einreichen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {showReviewModal && selectedInspection && (
          <div className="modal-overlay" onClick={closeReviewModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Inspektion bewerten</h2>
              <div className="inspection-info">
                <p><strong>Typ:</strong> {selectedInspection.type}</p>
                <p><strong>Anweisungen:</strong> {selectedInspection.instructions}</p>
                {selectedInspection.response && <p><strong>Antwort:</strong> {selectedInspection.response}</p>}
              </div>
              {formErrorReview && <div className="shared-form-error-banner">{formErrorReview}</div>}
              <form onSubmit={handleReview}>
                <div className="form-group">
                  <label>Bewertung: {review.score}/10</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={review.score}
                    onChange={e => setReview({...review, score: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Ergebnis</label>
                  <select
                    value={review.passed ? 'true' : 'false'}
                    onChange={e => setReview({...review, passed: e.target.value === 'true'})}
                  >
                    <option value="true">Bestanden</option>
                    <option value="false">Nicht bestanden</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Feedback</label>
                  <textarea
                    value={review.feedback}
                    onChange={e => setReview({...review, feedback: e.target.value})}
                    placeholder="Feedback zur Inspektion..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeReviewModal} disabled={savingReview}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingReview}>
                    {savingReview ? 'Speichert\u2026' : 'Bewertung speichern'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Inspections;
